import { type Matrix, CodingAPI } from '../api/coding';

export const STARTER_MATRICES: { name: string; targetVar: string; spec: Matrix }[] = [
  {
    name: 'Razones de voto',
    targetVar: 'encuesta/razon_voto1',
    spec: {
      question: '¿Por qué votaría por este candidato?',
      language: 'es',
      instructions: 'Clasifique los motivos principales de apoyo electoral manifestados por el encuestado.',
      origin: 'standard',
      normalizer: 'unicode-v1',
      dimensions: [
        { name: 'principal', label: 'Motivo principal', max_codes: 2, required: true },
      ],
      codes: [
        {
          value: 'PROPUESTAS',
          label: 'Buenas propuestas / Plan de trabajo',
          definition: 'Menciona ideas, planes de gobierno o propuestas específicas',
          dimension: 'principal',
          parent: null,
          inclusions: 'propuestas, proyectos, plan, ideas, plan de gobierno',
          exclusions: '',
          examples: ['tiene buenas propuestas', 'su plan de trabajo me convence', 'propone mejoras'],
          aliases: ['propuestas', 'buenas propuestas', 'su plan'],
          patterns: [],
        },
        {
          value: 'EXPERIENCIA',
          label: 'Experiencia / Capacidad de gestión',
          definition: 'Menciona experiencia previa, capacidad demostrada o trayectoria profesional',
          dimension: 'principal',
          parent: null,
          inclusions: 'experiencia, capaz, ya fue alcalde, sabe gobernar, preparado',
          exclusions: '',
          examples: ['ya tiene experiencia', 'sabe cómo gobernar', 'tiene capacidad de gestión'],
          aliases: ['experiencia', 'tiene experiencia', 'ya goberno'],
          patterns: [],
        },
        {
          value: 'HONESTIDAD',
          label: 'Honestidad / No corrupción',
          definition: 'Menciona honradez, transparencia, limpieza o no robar',
          dimension: 'principal',
          parent: null,
          inclusions: 'honesto, honrado, no roba, transparente, limpio, no corrupto',
          exclusions: '',
          examples: ['es una persona honesta', 'no roba como los demás', 'parece transparente'],
          aliases: ['honesto', 'honradez', 'no es corrupto', 'no roba'],
          patterns: [],
        },
        {
          value: 'OBRAS',
          label: 'Obras realizadas / Infraestructura',
          definition: 'Menciona obras previas, pistas, veredas, parques, colegios o infraestructura',
          dimension: 'principal',
          parent: null,
          inclusions: 'obras, pistas, parques, colegios, construyo, hizo obras',
          exclusions: '',
          examples: ['hizo bastantes obras', 'arregló las pistas', 'las obras que dejó'],
          aliases: ['hizo obras', 'obras', 'sus obras'],
          patterns: [],
        },
        {
          value: 'SEGURIDAD',
          label: 'Seguridad ciudadana / Orden',
          definition: 'Menciona lucha contra la delincuencia, serenazgo, cámaras o patrullaje',
          dimension: 'principal',
          parent: null,
          inclusions: 'seguridad, serenazgo, delincuencia, camaras, patrullaje, crimen',
          exclusions: '',
          examples: ['promete más seguridad', 'va a combatir la delincuencia', 'mejorará el serenazgo'],
          aliases: ['seguridad', 'mas seguridad', 'contra la delincuencia'],
          patterns: [],
        },
        {
          value: 'CAMBIO',
          label: 'Deseo de cambio / Renovación',
          definition: 'Menciona rostro nuevo, juventud, cansancio de los mismos de siempre',
          dimension: 'principal',
          parent: null,
          inclusions: 'cambio, renovacion, cara nueva, rostro nuevo, los de siempre fallaron',
          exclusions: '',
          examples: ['queremos un cambio', 'los de siempre ya no', 'es una opción nueva'],
          aliases: ['cambio', 'nueva opcion', 'renovacion'],
          patterns: [],
        },
        {
          value: 'CERCANIA',
          label: 'Cercanía a la gente / Humildad',
          definition: 'Menciona humildad, carisma, ayuda social o contacto directo con el pueblo',
          dimension: 'principal',
          parent: null,
          inclusions: 'humilde, cercano, ayuda a la gente, carismatico, del pueblo, buena gente',
          exclusions: '',
          examples: ['ayuda a los pobres', 'es humilde y escucha', 'camina con el pueblo'],
          aliases: ['humilde', 'cercano a la gente', 'ayuda social'],
          patterns: [],
        },
        {
          value: 'OTROS',
          label: 'Otros motivos específicos',
          definition: 'Cualquier otro motivo relevante que no encaja en las categorías anteriores',
          dimension: 'principal',
          parent: null,
          inclusions: '',
          exclusions: '',
          examples: ['porque me cae bien', 'por tradicion familiar'],
          aliases: [],
          patterns: [],
        },
      ],
      states: [
        { state: 'NS', aliases: ['no se', 'ns', 'no precisa', 'desconozco', 'no sabe'] },
        { state: 'NINGUNO', aliases: ['ninguno', 'ningun motivo', 'nadie', 'por nada'] },
        { state: 'RECHAZO', aliases: ['no responde', 'secreto', 'no quiere opinar', 'reservado'] },
      ],
    },
  },
  {
    name: 'Problemas y críticas al alcalde',
    targetVar: 'encuesta/peoralcalde',
    spec: {
      question: '¿Qué es lo peor que ha hecho el alcalde por el Distrito?',
      language: 'es',
      instructions: 'Clasifique las principales quejas, problemas o deficiencias señaladas sobre la gestión del alcalde.',
      origin: 'standard',
      normalizer: 'unicode-v1',
      dimensions: [
        { name: 'principal', label: 'Problema señalado', max_codes: 2, required: true },
      ],
      codes: [
        {
          value: 'INSEGURIDAD',
          label: 'Inseguridad ciudadana / Delincuencia',
          definition: 'Falta de serenazgo, robos, asaltos, falta de cámaras o patrullaje',
          dimension: 'principal',
          parent: null,
          inclusions: 'inseguridad, delincuencia, robos, no hay serenazgo, asaltos, falta de camaras',
          exclusions: '',
          examples: ['mucha delincuencia en el distrito', 'no se ve al serenazgo', 'los robos han aumentado'],
          aliases: ['inseguridad', 'delincuencia', 'robos'],
          patterns: [],
        },
        {
          value: 'FALTA_OBRAS',
          label: 'Falta de obras / Pistas en mal estado',
          definition: 'Pistas rotas, falta de mantenimiento de vías, obras abandonadas o de mala calidad',
          dimension: 'principal',
          parent: null,
          inclusions: 'no hace obras, pistas rotas, pistas en mal estado, huecos, obras botadas, no se ven obras',
          exclusions: '',
          examples: ['las pistas están llenas de huecos', 'no ha hecho ninguna obra importante', 'obras paralizadas'],
          aliases: ['pistas rotas', 'no hace obras', 'falta de obras', 'huecos en las pistas'],
          patterns: [],
        },
        {
          value: 'LIMPIEZA',
          label: 'Basura / Limpieza pública deficiente',
          definition: 'Acumulación de basura en calles, desmonte, falta de recojo de residuos',
          dimension: 'principal',
          parent: null,
          inclusions: 'basura, desmonte, suciedad, no recogen la basura, parques descuidados',
          exclusions: '',
          examples: ['acumulación de basura', 'las calles están sucias', 'el camión de basura no pasa'],
          aliases: ['basura', 'suciedad', 'no recoge basura'],
          patterns: [],
        },
        {
          value: 'CORRUPCION',
          label: 'Corrupción / Mal manejo de recursos',
          definition: 'Robo de fondos públicos, gastos innecesarios, favorecimiento o falta de transparencia',
          dimension: 'principal',
          parent: null,
          inclusions: 'corrupcion, robo, malgasto, plata, favorecimiento, sobreprecios',
          exclusions: '',
          examples: ['mucha corrupción en la municipalidad', 'se lleva la plata', 'malgasta los recursos'],
          aliases: ['corrupcion', 'roba', 'malgasto'],
          patterns: [],
        },
        {
          value: 'DESATENCION',
          label: 'No atiende al vecino / Promesas incumplidas',
          definition: 'Lejanía, prepotencia, abandono de sectores populares o promesas que no se cumplieron',
          dimension: 'principal',
          parent: null,
          inclusions: 'no atiende, no escucha, olvido, prepotente, promete y no cumple, abandonados',
          exclusions: '',
          examples: ['nos tiene en abandono', 'prometió mucho y no cumplió nada', 'no recibe a los vecinos'],
          aliases: ['abandono', 'no cumple', 'promesas incumplidas', 'no atiende'],
          patterns: [],
        },
        {
          value: 'TODO_BIEN',
          label: 'Nada malo / Todo bien / Buena gestión',
          definition: 'El vecino considera que no hay nada malo que reprocharle o aprueba su gestión',
          dimension: 'principal',
          parent: null,
          inclusions: 'nada, todo bien, nada malo, trabaja bien, buen alcalde, todo correcto',
          exclusions: '',
          examples: ['para mí todo está bien', 'no le veo nada malo', 'está trabajando bien'],
          aliases: ['nada malo', 'todo bien', 'nada'],
          patterns: [],
        },
        {
          value: 'OTROS',
          label: 'Otros problemas específicos',
          definition: 'Otras críticas no clasificadas en las categorías anteriores',
          dimension: 'principal',
          parent: null,
          inclusions: '',
          exclusions: '',
          examples: ['el tráfico en el óvalo', 'el desorden comercial'],
          aliases: [],
          patterns: [],
        },
      ],
      states: [
        { state: 'NS', aliases: ['no se', 'ns', 'no opina', 'desconozco', 'no sabe'] },
        { state: 'NINGUNO', aliases: ['ninguno', 'nada', 'ningun problema'] },
        { state: 'RECHAZO', aliases: ['no responde', 'secreto', 'no quiere hablar'] },
      ],
    },
  },
  {
    name: 'Observaciones y comentarios de campo',
    targetVar: 'encuesta/comentarios',
    spec: {
      question: 'Observaciones y comentarios adicionales del encuestado',
      language: 'es',
      instructions: 'Clasifique el tono y contenido de las observaciones generales de campo.',
      origin: 'standard',
      normalizer: 'unicode-v1',
      dimensions: [
        { name: 'tono', label: 'Tono general', max_codes: 1, required: true },
      ],
      codes: [
        {
          value: 'POSITIVO',
          label: 'Comentario positivo o de apoyo',
          definition: 'Expresa satisfacción, felicitación o respaldo',
          dimension: 'tono',
          parent: null,
          inclusions: 'bien, felicitaciones, buen trabajo, conforme, apoyo',
          exclusions: '',
          examples: ['buena encuesta', 'espero que gane el mejor', 'todo conforme'],
          aliases: ['positivo', 'conforme', 'bien'],
          patterns: [],
        },
        {
          value: 'NEGATIVO',
          label: 'Queja, reclamo o inconformidad',
          definition: 'Expresa molestia, frustración, crítica o descontento general',
          dimension: 'tono',
          parent: null,
          inclusions: 'molesto, queja, reclamo, descontento, hartazgo, estafa',
          exclusions: '',
          examples: ['estamos cansados de la política', 'siempre es lo mismo', 'no creo en nadie'],
          aliases: ['negativo', 'queja', 'molestia'],
          patterns: [],
        },
        {
          value: 'SUGERENCIA',
          label: 'Propuesta o sugerencia para la zona',
          definition: 'Aporta ideas o solicitudes de mejora concretas',
          dimension: 'tono',
          parent: null,
          inclusions: 'sugerencia, pedir, que vengan, que arreglen, apoyo social',
          exclusions: '',
          examples: ['pedimos que pongan luz en este parque', 'que haya más patrullaje'],
          aliases: ['sugerencia', 'pedido', 'propuesta'],
          patterns: [],
        },
        {
          value: 'INCIDENCIA',
          label: 'Incidencia técnica o del proceso de encuesta',
          definition: 'Dificultades en la entrevista, interrupciones o aclaraciones del encuestador',
          dimension: 'tono',
          parent: null,
          inclusions: 'apurado, interrumpio, familiar ayudo, corto la llamada, no termino',
          exclusions: '',
          examples: ['encuestado con prisa', 'interrupción por lluvia', 'ayudado por su hija'],
          aliases: ['incidencia', 'incidencia de campo'],
          patterns: [],
        },
        {
          value: 'OTROS',
          label: 'Otros comentarios',
          definition: 'Comentarios varios no clasificados',
          dimension: 'tono',
          parent: null,
          inclusions: '',
          exclusions: '',
          examples: [],
          aliases: [],
          patterns: [],
        },
      ],
      states: [
        { state: 'VACIA', aliases: ['ninguna', 'sin comentarios', 'sin observaciones', 'nada'] },
      ],
    },
  },
];

/**
 * Crea y publica automáticamente las matrices recomendadas en el proyecto.
 */
export async function seedStarterMatrices(projectId: number): Promise<{ created: number; published: number }> {
  let created = 0;
  let published = 0;

  for (const starter of STARTER_MATRICES) {
    try {
      const res = (await CodingAPI.createBook(projectId, starter.name, starter.spec)) as {
        id: number;
        version: { id: number };
      };
      created++;
      if (res?.version?.id) {
        await CodingAPI.publish(res.version.id);
        published++;
      }
    } catch {
      // Si ya existía una matriz con ese nombre, continuar con las demás
    }
  }

  return { created, published };
}
